<template lang="pug">
el-drawer.document.bookings-pdf-drawer(
    :title="drawerTitle"
    :visible.sync="visibleSync"
    :wrapper-closable="false"
    :modal="true"
    :size="drawerSize"
    @open="fitDrawer"
    @closed="resetDocument"
)
    template(v-if="pdfUrl")
        el-form(v-loading="loading")
            section.body(v-if="error === false")
                .preview(
                    ref="preview"
                    :class="{ loaded: !loading }"
                )
                    pdf.pdf(
                        ref="pdf-preview"
                        :src="pdfUrl"
                        :page="pagination.current"
                        @num-pages="total => pagination.total = total"
                        @loaded="previewLoaded"
                        @error="previewError"
                    )
    .drawer-footer
        el-button(
            round
            size="mini"
            icon="el-icon-fa-times fas"
            @click="handleClose"
        ) {{ $t('Dismiss') }}
        .preview-pagination(v-if="pagination.total > 1")
            el-pagination(
                small
                layout="prev, pager, next"
                :pager-count="5"
                :page-size="1"
                :total="pagination.total"
                @current-change="current => updatePagination(current)"
            )
        .right-buttons
            el-button(
                circle
                size="mini"
                type="primary"
                :disabled="loading"
                icon="el-icon-fa-download fas"
                @click="downloadPdf"
            )
            el-button(
                round
                size="mini"
                type="primary"
                :disabled="loading"
                icon="el-icon-fa-print fas"
                @click="printPdf"
            ) {{ $t('Print') }}
</template>

<i18n>
{
    "de": {
        "Bookings PDF": "Anmeldungen (PDF)",
        "QR code PDF": "QR-Code (PDF)",
        "Dismiss": "Schließen",
        "Print": "Drucken",
        "An error occurred!": "Ein Fehler ist aufgetreten!",
        "Preview could not be rendered": "Vorschau konnte nicht generiert werden",
        "Missing PDF": "PDF konnte nicht gefunden werden"
    }
}
</i18n>

<script>
import pdf from 'vue-pdf'
import printJS from 'print-js-updated'

import EventApi from '@/extensions/eventManager/store/api/event'
import { ensureFreshToken } from '@/helpers'
import { a4DrawerSize, a4DrawerSizeEstimate } from '@/utils/a4DrawerSize'

export default {
    name: 'BookingsPdfDrawer',
    components: {
        pdf
    },
    props: {
        visible: {
            type: Boolean,
            default: false
        },
        eventId: {
            type: [Number, String],
            default: null
        },
        columns: {
            type: Array,
            default: () => []
        },
        sort: {
            type: String,
            default: 'name'
        },
        landscape: {
            type: Boolean,
            default: false
        },
        showQrCode: {
            type: Boolean,
            default: true
        },
        loginEmail: {
            type: String,
            default: ''
        },
        qrOnly: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            loading: false,
            error: false,
            drawerSize: a4DrawerSizeEstimate(),
            pagination: {
                current: 1,
                total: 1
            }
        }
    },
    computed: {
        visibleSync: {
            get() {
                return this.visible
            },
            set(value) {
                if (!value) {
                    this.$emit('close')
                }
            }
        },
        drawerTitle() {
            return this.qrOnly ? this.$t('QR code PDF') : this.$t('Bookings PDF')
        },
        pdfUrl() {
            return this.buildPdfUrl()
        }
    },
    mounted() {
        window.addEventListener('resize', this.fitDrawer)
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.fitDrawer)
    },
    watch: {
        visible(value) {
            if (value) {
                this.loading = true
                this.error = false
                this.pagination = { current: 1, total: 1 }
            }
        }
    },
    methods: {
        buildPdfUrl() {
            if (!this.eventId) return null
            return EventApi.bookingsPdfUrl(this.eventId, {
                columns: this.columns,
                sort: this.sort,
                landscape: this.landscape,
                showQrCode: this.showQrCode,
                loginEmail: this.loginEmail,
                qrOnly: this.qrOnly,
            })
        },
        // The drawer is as wide as a whole A4 page needs at this window height:
        // an estimate the moment it starts to open, the measured figure a tick
        // later, so it never slides in wide and snaps back.
        fitDrawer() {
            this.drawerSize = a4DrawerSizeEstimate()
            this.$nextTick(() => {
                this.drawerSize = a4DrawerSize(this.$el) || this.drawerSize
            })
        },
        handleClose() {
            this.$emit('close')
        },
        resetDocument() {
            this.loading = false
            this.error = false
            this.pagination = { current: 1, total: 1 }
        },
        previewLoaded() {
            setTimeout(() => {
                this.loading = false
            }, 150)
        },
        previewError(error) {
            let message = {
                title: this.$t('An error occurred!'),
                message: this.$t('Preview could not be rendered'),
                iconClass: 'fas el-icon-fa-times',
                position: 'bottom-left',
                customClass: 'dark',
                duration: 8000
            }

            if (error.name === 'MissingPDFException') {
                message.message = this.$t('Missing PDF')
            }

            this.resetDocument()
            this.$emit('close')
            this.$notify.error(message)
        },
        updatePagination(current) {
            if (this.$refs.preview && this.$refs.preview.scrollTop) {
                this.$refs.preview.scroll({
                    top: 0,
                    left: 0,
                    behavior: 'smooth'
                })
            }

            setTimeout(() => {
                this.pagination.current = current
            }, this.$refs.preview && this.$refs.preview.scrollTop ? 200 : 0)
        },
        async downloadPdf() {
            if (!this.pdfUrl) return

            // Built after the refresh so the link carries a fresh token.
            await ensureFreshToken()
            const element = document.createElement('a')
            element.setAttribute('href', `${this.buildPdfUrl()}&download`)
            element.style.display = 'none'
            document.body.appendChild(element)
            element.click()
            document.body.removeChild(element)
        },
        async printPdf() {
            if (!this.pdfUrl) return

            await ensureFreshToken()
            printJS({ printable: this.buildPdfUrl() })
        }
    }
}
</script>

<style lang="sass" scoped>
.el-drawer__wrapper.document
    ::v-deep .el-drawer
        min-width: 500px
        max-width: 90%
        .el-form
            display: flex
            flex-direction: column
            .body
                position: relative
                flex-grow: 1
                width: 100%
        .preview
            position: absolute
            left: 0
            top: 0
            width: 100%
            height: 100%
            max-width: 100%
            max-height: 100%
            overflow: auto
    .preview
        padding: 2rem 20px
        display: flex
        align-items: center
        justify-content: center
        & > span
            width: 100%
            flex-grow: 1
            margin: auto 0
        &.loaded
            background: #babfc3
            & > span
                box-shadow: 0 4px 5px 0 rgba(0,0,0,0.14), 0 1px 10px 0 rgba(0,0,0,0.12), 0 2px 4px -1px rgba(0,0,0,0.3)

    .drawer-footer
        display: flex
        justify-content: space-between
        align-items: center
        padding: 10px 20px
        border-top: 1px solid #ebeef5
        .right-buttons
            display: flex
            gap: 8px
</style>
